import Big from 'big.js';
import { Formik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Currency, DateTime, Drawer, Field, Form, RouteLink, Table } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck } from '~/hooks';
import { dateFormats, emptyStringToNull, mergeValues } from '~/utils';
import { isPrepaymentLocked, prepaymentTypes } from './prepaymentTypes';

const AppliedInvoices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export default function PrepaymentForm({ prepaymentId, projectId, currency, readOnly = false, onSaved, onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();

  const [prepayment, setPrepayment] = useState(null);
  useEffect(() => {
    (async () => {
      if (!prepaymentId) {
        setPrepayment({});
        return;
      }

      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .prepayments(prepaymentId)
          .get();
        setPrepayment(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId, prepaymentId]);

  // Every prepayment of a type shares one invoice item, so the form has to know what its peers already use.
  const [peers, setPeers] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).projectSettings(projectId).prepayments().get();
        setPeers(data.filter((peer) => peer.id !== prepaymentId));
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId, prepaymentId]);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  if (!prepayment || !peers) return null;

  const isNew = !prepaymentId;
  // A locked prepayment and a member without the billing section both end up view-only.
  const locked = readOnly || (!isNew && isPrepaymentLocked(prepayment));

  const invoiceItemForType = (type) => peers.find((peer) => peer.type === type)?.invoiceItem ?? null;

  const initialValues = mergeValues(
    {
      date: moment().format(dateFormats.isoDate),
      type: 'services',
      invoiceItem: invoiceItemForType(prepayment.type ?? 'services'),
      details: '',
      quantity: '',
      rate: '',
      fee: '',
      includeQuantity: prepayment.quantity > 0,
    },
    prepayment,
  );

  const handleSubmit = async (values, formik) => {
    formik.setSubmitting(true);
    values = emptyStringToNull({
      ..._.omit(values, ['invoiceItem', 'includeQuantity']),
      invoiceItemId: values.invoiceItem.id,
    });

    try {
      if (prepaymentId) {
        await api.www.workspaces(workspace.id).projectSettings(projectId).prepayments(prepaymentId).patch(values);
      } else {
        await api.www.workspaces(workspace.id).projectSettings(projectId).prepayments().post(values);
      }

      toast.success('Prepayment saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
    }

    formik.setSubmitting(false);
  };

  return (
    <Drawer
      title={locked ? 'View Prepayment' : isNew ? 'Add Prepayment' : 'Edit Prepayment'}
      isOpen
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              date: Yup.date().label('Bill Date').nullable().required(),
              type: Yup.string()
                .label('Type')
                .oneOf(prepaymentTypes.map((type) => type.id))
                .required(),
              invoiceItem: Yup.mixed().label('Invoice Item').required(),
              details: Yup.string().label('Description').max(255).required(),
              quantity: Yup.number()
                .label('Quantity')
                .positive()
                .max(99999999.99)
                .nullable()
                .when('includeQuantity', (includeQuantity, schema) => (includeQuantity ? schema.required() : schema)),
              rate: Yup.number()
                .label('Rate')
                .min(-99999999999)
                .max(99999999999)
                .nullable()
                .when('includeQuantity', (includeQuantity, schema) => (includeQuantity ? schema.required() : schema)),
              fee: Yup.number().label('Amount').min(-99999999999).max(99999999999).required(),
            })}>
            {(formik) => {
              const handleIncludeQuantityChange = ({ target: { checked } }) => {
                formik.setValues({ ...formik.values, includeQuantity: checked, quantity: '', rate: '', fee: '' });
              };

              const handleCalculateFee = () => {
                const quantity = _.isNumber(formik.values.quantity)
                  ? _.round(formik.values.quantity, 2)
                  : formik.values.quantity;

                const rate = _.isNumber(formik.values.rate) ? _.round(formik.values.rate, 7) : formik.values.rate;

                const fee =
                  _.isNumber(formik.values.quantity) && _.isNumber(formik.values.rate)
                    ? Big(quantity).times(rate).round(2).toNumber()
                    : '';

                formik.setValues({ ...formik.values, fee, quantity, rate });
              };

              const handleTypeChange = ({ target: { value } }) => {
                formik.setValues({
                  ...formik.values,
                  type: value,
                  invoiceItem: invoiceItemForType(value) ?? formik.values.invoiceItem,
                });
              };

              return (
                <Form>
                  <Form.Section title="Details">
                    <Form.Control>
                      <Field.DayPicker name="date" placeholder="Bill Date" clearable={false} disabled={locked} />
                    </Form.Control>

                    <Form.Control>
                      <Field.SingleSelect
                        name="type"
                        placeholder="Type"
                        disabled={locked || !isNew}
                        onChange={handleTypeChange}>
                        {prepaymentTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </Field.SingleSelect>
                    </Form.Control>

                    <Form.Control>
                      <Field.InvoiceItemSelect
                        name="invoiceItem"
                        placeholder="Invoice Item"
                        type="income"
                        position="top"
                        clearable={false}
                        disabled={locked || !!invoiceItemForType(formik.values.type)}
                      />
                    </Form.Control>

                    <Form.Control>
                      <Field.Text name="details" placeholder="Description" maxLength={255} disabled={locked} />
                    </Form.Control>
                  </Form.Section>

                  <Form.Section title="Billing">
                    {!locked && (
                      <Form.Control>
                        <Field.Checkbox
                          name="includeQuantity"
                          label="Include a quantity and rate for this prepayment"
                          onChange={handleIncludeQuantityChange}
                        />
                      </Form.Control>
                    )}

                    {formik.values.includeQuantity && (
                      <Form.Control>
                        <Field.Number
                          name="quantity"
                          placeholder="Quantity"
                          precision={2}
                          onBlur={handleCalculateFee}
                          disabled={locked}
                        />
                        <Field.Currency
                          name="rate"
                          placeholder="Rate"
                          precision={7}
                          currency={currency}
                          onBlur={handleCalculateFee}
                          disabled={locked}
                        />
                      </Form.Control>
                    )}

                    <Form.Control>
                      <Field.Currency
                        name="fee"
                        placeholder="Amount"
                        disabled={formik.values.includeQuantity || locked}
                        currency={currency}
                      />
                    </Form.Control>
                  </Form.Section>

                  <AppliedInvoicesSection prepayment={prepayment} currency={currency} />

                  <Drawer.Actions>
                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                      {!locked && (
                        <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                          Save &amp; Close
                        </Button>
                      )}
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

// Invoices that drew this prepayment down. The origination invoice — where the prepayment was billed
// up front — is left out: none of its amount is "used", so listing it here read as misleading.
function AppliedInvoicesSection({ prepayment, currency }) {
  const { workspace } = useWorkspace();

  const rows = (prepayment.invoiceLinePrepayments ?? [])
    .map((usage) => ({ amountUsed: usage.amount, invoice: usage.invoiceLine?.invoice }))
    .filter((row) => row.invoice);

  if (!rows.length) return null;

  return (
    <Form.Section title="Applied to Invoices">
      <AppliedInvoices>
        <Table small>
          <Table.Header>
            <Table.Column width="6rem">#</Table.Column>
            <Table.Column width="8rem">Issued</Table.Column>
            <Table.Column align="right">Amount Used</Table.Column>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => (
              <Table.Row key={row.invoice.id}>
                <Table.Cell>
                  <RouteLink to={`/app/${workspace.key}/billing/invoices/${row.invoice.id}`}>
                    {row.invoice.number}
                  </RouteLink>
                </Table.Cell>
                <Table.Cell>
                  <DateTime value={row.invoice.issuedOn} />
                </Table.Cell>
                <Table.Cell>
                  <Currency value={row.amountUsed} currency={currency} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </AppliedInvoices>
    </Form.Section>
  );
}
